import { useEffect, useRef, useState } from 'react';
import { setHardwareTrigger } from '../services/printerService';
import { erkenneEtikettBox, isTurboAutoFotoOn, setTurboAutoFotoOn } from '../services/turboPaketPilot';

/**
 * 📷 In-App-Kamera (Ausweich-Weg, pro Gerät per Opt-in)
 *
 * Hintergrund: Der normale Weg (<input capture>) startet die System-Kamera-App. Ab
 * Android 11 muss die APK dafür im Manifest <queries> für IMAGE_CAPTURE deklarieren —
 * fehlt das (APK ≤ 2.2.0), findet Capacitor auf manchen Handys KEINE Kamera-App und
 * öffnet still die Galerie. Diese Kamera läuft direkt in der WebView (getUserMedia)
 * und braucht keine fremde App → funktioniert per Web-Deploy, ohne neues APK.
 *
 * Liefert das Foto als File an onCapture — gleicher Weg wie der Datei-Input.
 */

const SETTING_KEY = 'kiosk_inapp_camera';

export function isInAppCameraEnabled() {
  try { return localStorage.getItem(SETTING_KEY) === '1'; } catch { return false; }
}
export function setInAppCameraEnabled(on) {
  try { localStorage.setItem(SETTING_KEY, on ? '1' : '0'); } catch { /* */ }
}

// 🔦 Taschenlampe: an diesem Gerät gemerkt (bleibt im Serienmodus von Foto zu Foto an). Standard: aus.
const TORCH_KEY = 'kiosk_inapp_torch';
function torchGemerkt() {
  try { return localStorage.getItem(TORCH_KEY) === '1'; } catch { return false; }
}

// bleibtOffen (Turbo PaketPilot): nach dem Auslösen läuft die Kamera weiter, kurzer Blitz als Rückmeldung;
// children: eigene Anzeige über dem Bild, unten: unter dem Auslöser; onCapture(file, { schaerfe, thumb, auto }) — schaerfe = Varianz des
// Laplace-Filters auf einem 200-px-Graubild (klein = unscharf), nur als Hinweis
// demo: keine echte Kamera (Turbo-Vorschau) — Platzhalterbild, der Auslöser liefert onCapture(null, { auto })
// onAusgeloest: sobald das Foto aufgenommen ist (vor dem JPEG-Kodieren) — Rückmeldung „kannst weiter" ohne die ~0,1–0,2 s Kodierzeit
export default function InAppCamera({ onCapture, onClose, t, hudTexte = null, hudAn = true, autoFotoAn = null, onToggleAutoFoto = null, videoMaxHeight = null, hint, cancelLabel, onError, bleibtOffen = false, children = null, unten = null, demo = false, onAusgeloest = null }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [torchMoeglich, setTorchMoeglich] = useState(false); // nur wenn die Kamera ein Licht meldet
  const [torchAn, setTorchAn] = useState(false);
  const [autoFotoIntern, setAutoFotoIntern] = useState(() => isTurboAutoFotoOn());
  const autoFoto = autoFotoAn != null ? autoFotoAn : autoFotoIntern;
  // 🎯 Live-AR-Rahmen im Kamerastream (nur bei bleibtOffen aktiv)
  const [hud, setHud] = useState({ gefunden: false, x: 0.18, y: 0.16, w: 0.64, h: 0.68, ox: 0, oy: 0, rw: 1, rh: 1, status: 'suche', hold: 0 });
  const prevGrayRef = useRef(null);
  const scanCanvasRef = useRef(null);
  const autoHoldRef = useRef(0);
  const autoGesperrtRef = useRef(false);
  const unlockFramesRef = useRef(0);
  const lastAutoShotRef = useRef(0);
  const lastShotAtRef = useRef(0);

  const schalteTorch = async (an, merken = true) => {
    try {
      const track = streamRef.current && streamRef.current.getVideoTracks()[0];
      if (!track) return;
      await track.applyConstraints({ advanced: [{ torch: !!an }] });
      setTorchAn(!!an);
      if (merken) { try { localStorage.setItem(TORCH_KEY, an ? '1' : '0'); } catch { /* */ } }
    } catch { /* Gerät lässt das Licht nicht schalten → Knopf bleibt, Zustand unverändert */ }
  };

  useEffect(() => {
    if (demo) { setReady(true); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          // 13.10.2026: Vorschau etwas größer; das eigentliche Foto kommt per ImageCapture in Sensor-Auflösung (s. ausloesen),
          // ein 1920×1440-Videobild reichte nicht: Empfängerzeile ~10 px hoch → Gemini riet den Namen (Green Goat „Van De Winter")
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1920 } },
        });
        if (cancelled) { stream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = stream;
        // Autofokus nur, wenn das Gerät ihn meldet (blind gesetzt → „setPhotoOptions failed")
        try {
          const track = stream.getVideoTracks()[0];
          const caps = (track.getCapabilities && track.getCapabilities()) || {};
          if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
            track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
          }
          // 🔦 Licht nur anbieten, wenn die Kamera eines hat; zuletzt gewählten Zustand wiederherstellen
          if (caps.torch) {
            setTorchMoeglich(true);
            if (torchGemerkt()) schalteTorch(true, false);
          }
          // 📸 PhotoCapabilities schon beim Kamerastart abfragen (spart ~150 ms beim ersten Auslösen)
          // und Foto-Breite nahe MAX_KANTE (2600 px) wählen statt 12–50 MP (viel schnelleres JPEG/Bitmap-Dekodieren)
          if (typeof window.ImageCapture === 'function') {
            try {
              const ic = new window.ImageCapture(track);
              if (typeof ic.getPhotoCapabilities === 'function') {
                ic.getPhotoCapabilities().then((pc) => {
                  const opts = {};
                  if (pc && Array.isArray(pc.fillLightMode) && pc.fillLightMode.includes('off')) {
                    opts.fillLightMode = 'off';
                  }
                  if (pc && pc.imageWidth && typeof pc.imageWidth.max === 'number' && pc.imageWidth.max >= 2400) {
                    const ziel = Math.max(pc.imageWidth.min || 2400, Math.min(pc.imageWidth.max, 2688));
                    opts.imageWidth = pc.imageWidth.step
                      ? Math.round(ziel / pc.imageWidth.step) * pc.imageWidth.step
                      : ziel;
                  }
                  photoOptsRef.current = opts;
                }).catch(() => { photoOptsRef.current = {}; });
              }
            } catch { /* */ }
          }
        } catch { /* */ }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch { /* */ }
        }
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        if (onError) { onError(e); return; } // Aufrufer entscheidet (Serienmodus → normaler Weg)
        setError(t('camera.permissionHint'));
      }
    })();
    return () => {
      cancelled = true;
      try { if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch { /* */ }
      streamRef.current = null;
    };
  }, [t, demo]);

  // Längste Kante des hochgeladenen Fotos: genug Detail für kleine Etikettschrift, ohne Riesen-Uploads
  const MAX_KANTE = 2600;
  const canvasZuDatei = (canvas) => {
    // synchron (toDataURL): canvas.toBlob wird in der WebView gedrosselt (siehe Station-Latenz)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const bin = atob(dataUrl.split(',')[1]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], `paket_${Date.now()}.jpg`, { type: 'image/jpeg' });
  };
  const zeichneVerkleinert = (quelle, w, h) => {
    const sc = Math.min(1, MAX_KANTE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * sc);
    canvas.height = Math.round(h * sc);
    canvas.getContext('2d').drawImage(quelle, 0, 0, canvas.width, canvas.height);
    return canvas;
  };
  // Ist der laufende Videostream bereits hochauflösend (>= 2400 px, z. B. 2560×1920), im Turbo-Modus (bleibtOffen)
  // oder bei aktiver Taschenlampe (torchAn), nehmen wir SOFORT (0 ms) das scharfe Live-Videobild.
  // Liefert der Videostream hingegen nur <= 1920 px (viele Android-WebViews drosseln <video> trotz ideal:2560 auf 1920×1440/1080),
  // holen wir über ImageCapture.takePhoto() ein ~2600-px-Foto (13.10.2026 Green Goat „Van De Winter": 10-px-Schrift bei 1920×1440),
  // aber mit vorab geladenem fillLightMode:'off' + ~2600 px Zielbreite statt langsamem 12-MP-Vorblitz-Zyklus.
  const photoOptsRef = useRef(null);
  const takePhotoSlowRef = useRef(false);
  const fotoInVollerAufloesung = async (v) => {
    if (bleibtOffen || torchAn || takePhotoSlowRef.current) return null;
    if (v && Math.max(v.videoWidth || 0, v.videoHeight || 0) >= 2400) return null;
    const track = streamRef.current && streamRef.current.getVideoTracks()[0];
    if (!track || typeof window.ImageCapture !== 'function') return null;
    const ic = new window.ImageCapture(track);
    const opts = photoOptsRef.current && Object.keys(photoOptsRef.current).length > 0 ? photoOptsRef.current : undefined;
    const t0 = Date.now();
    const schuss = opts ? ic.takePhoto(opts).catch(() => ic.takePhoto()) : ic.takePhoto();
    const blob = await Promise.race([
      schuss,
      new Promise((_, rej) => setTimeout(() => {
        takePhotoSlowRef.current = true;
        rej(new Error('takePhoto timeout'));
      }, 1800)),
    ]);
    if (Date.now() - t0 > 1500) takePhotoSlowRef.current = true;
    const bmp = await createImageBitmap(blob);
    try {
      if (v && v.videoWidth && v.videoHeight) {
        // Nur verwerfen, wenn takePhoto() ein schmaleres Format liefert als die Vorschau (z. B. 9:16-Foto bei 3:4-Vorschau
        // schneidet links/rechts 25 % ab!). Ein 4:3-Foto bei 16:9-Vorschau (bRatio < vRatio) zeigt hingegen MEHR und bleibt erhalten.
        const vRatio = Math.max(v.videoWidth, v.videoHeight) / Math.min(v.videoWidth, v.videoHeight);
        const bRatio = Math.max(bmp.width, bmp.height) / Math.min(bmp.width, bmp.height);
        if (bRatio - vRatio > 0.04) return null;
      }
      return zeichneVerkleinert(bmp, bmp.width, bmp.height);
    } finally { try { bmp.close(); } catch { /* */ } }
  };
  const [loest, setLoest] = useState(false);
  const loestRef = useRef(false);
  loestRef.current = loest;
  const [blitz, setBlitz] = useState(false);
  // Schärfe grob schätzen (~2 ms): Laplace-Varianz auf einem kleinen Graubild
  const schaerfeVon = (canvas) => {
    try {
      const w = 200; const h = Math.max(1, Math.round((canvas.height / canvas.width) * w));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true }); ctx.drawImage(canvas, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;
      const g = new Float32Array(w * h);
      for (let i = 0; i < w * h; i++) g[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
      let sum = 0; let sum2 = 0; let n = 0;
      for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
        const i = y * w + x; const l = g[i - 1] + g[i + 1] + g[i - w] + g[i + w] - 4 * g[i];
        sum += l; sum2 += l * l; n++;
      }
      return n ? sum2 / n - (sum / n) ** 2 : null;
    } catch { return null; }
  };
  // Kleines Vorschaubild (max. 360 px Kante) für die Prüf-Liste + Vergrößerung am Ende der Turbo-Runde (~2 ms)
  const thumbVon = (canvas) => {
    try {
      const maxK = 360;
      const sc = Math.min(1, maxK / Math.max(canvas.width, canvas.height));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(canvas.width * sc));
      c.height = Math.max(1, Math.round(canvas.height * sc));
      c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.72);
    } catch { return null; }
  };
  const ausloesen = async (istAuto = false) => {
    autoGesperrtRef.current = true;
    autoHoldRef.current = 0;
    unlockFramesRef.current = 0;
    lastAutoShotRef.current = Date.now();
    if (demo) { setBlitz(true); setTimeout(() => setBlitz(false), 150); if (onAusgeloest) onAusgeloest(); onCapture(null, { auto: istAuto === true }); return; }
    const v = videoRef.current;
    if (!v || !v.videoWidth || loestRef.current) return;
    loestRef.current = true;
    setLoest(true);
    let canvas = null;
    try { canvas = await fotoInVollerAufloesung(v); } catch { canvas = null; }
    if (!canvas) canvas = zeichneVerkleinert(v, v.videoWidth, v.videoHeight);
    if (bleibtOffen) {
      // Foto ist im Kasten → sofort Blitz/Ton/Vibration, dann erst kodieren (toDataURL blockiert ~0,1–0,2 s)
      setBlitz(true); setTimeout(() => setBlitz(false), 150);
      loestRef.current = false;
      setLoest(false);
      if (onAusgeloest) onAusgeloest();
      await new Promise((r) => setTimeout(r, 0)); // Blitz zeichnen lassen
      const schaerfe = schaerfeVon(canvas);
      const thumb = thumbVon(canvas);
      onCapture(canvasZuDatei(canvas), { schaerfe, thumb, auto: istAuto === true });
      return;
    }
    const file = canvasZuDatei(canvas);
    try { if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch { /* */ }
    onCapture(file);
  };
  const ausloesenRef = useRef(ausloesen);
  ausloesenRef.current = ausloesen;

  // 🔘 Physische Lautstärke-Tasten (Volume + / -) als Auslöser im Turbo-Modus einfangen
  useEffect(() => {
    if (!bleibtOffen) return undefined;
    const hwTrigger = () => {
      const now = Date.now();
      // 1,2 s Sperrzeit gegen Serien-Events beim Gedrückthalten der Lautstärketaste (Android dispatchKeyEvent)
      if (now - lastShotAtRef.current < 1200) return;
      lastShotAtRef.current = now;
      ausloesenRef.current(false);
    };
    const onKey = (e) => {
      if (e.repeat) return;
      if (e.key === 'AudioVolumeUp' || e.key === 'AudioVolumeDown' || e.key === 'VolumeUp' || e.key === 'VolumeDown') {
        e.preventDefault();
        hwTrigger();
      }
    };
    window.addEventListener('bench:trigger', hwTrigger);
    window.addEventListener('keydown', onKey);
    setHardwareTrigger(true);
    return () => {
      window.removeEventListener('bench:trigger', hwTrigger);
      window.removeEventListener('keydown', onKey);
      setHardwareTrigger(false);
    };
  }, [bleibtOffen]);

  // 🎯 Live-Etiketten-Kanten & Schärfe-Ampel im Videostream (~7 FPS, pausiert während Foto-Aufnahme; komplett aus wenn !hudAn)
  useEffect(() => {
    autoHoldRef.current = 0;
    autoGesperrtRef.current = false;
  }, [autoFoto]);

  useEffect(() => {
    if (!bleibtOffen || !ready || !hudAn) return undefined;
    if (demo) {
      let tick = 0;
      const iv = setInterval(() => {
        tick++;
        const phase = tick % 14;
        const status = phase < 3 ? 'nah' : phase < 6 ? 'fokus' : 'bereit';
        const x = status === 'nah' ? 0.31 : 0.22;
        const y = status === 'nah' ? 0.28 : 0.17;
        const w = status === 'nah' ? 0.38 : 0.56;
        const h = status === 'nah' ? 0.42 : 0.64;
        const hold = status === 'bereit' ? Math.min(6, phase - 5) : 0;
        setHud({ gefunden: true, x, y, w, h, ox: 0, oy: 0, rw: 1, rh: 1, status, hold });
        if (autoFoto && hold === 6) ausloesenRef.current(true);
      }, 220);
      return () => clearInterval(iv);
    }
    const sw = 160;
    const sh = 120;
    const iv = setInterval(() => {
      if (loestRef.current) return;
      const v = videoRef.current;
      if (!v || !v.videoWidth || !v.videoHeight) return;
      try {
        if (!scanCanvasRef.current) {
          const c = document.createElement('canvas');
          c.width = sw; c.height = sh;
          scanCanvasRef.current = c;
        }
        const ctx = scanCanvasRef.current.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, sw, sh);
        const rgba = ctx.getImageData(0, 0, sw, sh).data;
        const det = erkenneEtikettBox(rgba, sw, sh, prevGrayRef.current);
        prevGrayRef.current = det.gray;

        // Sichtbaren Bildbereich im <video> (object-fit: contain) berechnen, damit der Rahmen im Hochformat exakt sitzt
        let ox = 0, oy = 0, rw = 1, rh = 1;
        const cw = v.clientWidth;
        const ch = v.clientHeight;
        if (cw > 0 && ch > 0) {
          const cRatio = cw / ch;
          const vRatio = v.videoWidth / v.videoHeight;
          if (vRatio < cRatio) {
            rw = vRatio / cRatio;
            ox = (1 - rw) / 2;
          } else if (vRatio > cRatio) {
            rh = cRatio / vRatio;
            oy = (1 - rh) / 2;
          }
        }

        // Nach einem Schuss erst wieder freigeben, wenn mind. 1,8 s vergangen sind UND 3 Bilder lang bewegt/entfernt wurde
        if (autoGesperrtRef.current) {
          if (det.motion > 14 || !det.gefunden) {
            unlockFramesRef.current++;
            if (unlockFramesRef.current >= 3 && Date.now() - lastAutoShotRef.current > 1800) {
              autoGesperrtRef.current = false;
              unlockFramesRef.current = 0;
            }
          } else {
            unlockFramesRef.current = 0;
          }
        }
        if (det.status === 'bereit' && !autoGesperrtRef.current) {
          autoHoldRef.current = Math.min(6, autoHoldRef.current + 1);
        } else {
          autoHoldRef.current = 0;
        }
        const hold = autoHoldRef.current;

        setHud((prev) => {
          const nx = det.gefunden ? prev.x * 0.45 + det.x * 0.55 : 0.18;
          const ny = det.gefunden ? prev.y * 0.45 + det.y * 0.55 : 0.16;
          const nw = det.gefunden ? prev.w * 0.45 + det.w * 0.55 : 0.64;
          const nh = det.gefunden ? prev.h * 0.45 + det.h * 0.55 : 0.68;
          if (
            prev.gefunden === det.gefunden &&
            prev.status === det.status &&
            prev.hold === hold &&
            Math.abs(prev.x - nx) < 0.012 &&
            Math.abs(prev.y - ny) < 0.012 &&
            Math.abs(prev.w - nw) < 0.012 &&
            Math.abs(prev.h - nh) < 0.012 &&
            Math.abs(prev.ox - ox) < 0.01
          ) {
            return prev;
          }
          return { gefunden: det.gefunden, x: nx, y: ny, w: nw, h: nh, ox, oy, rw, rh, status: det.status, hold };
        });

        if (autoFoto && hold >= 6 && !autoGesperrtRef.current) {
          autoGesperrtRef.current = true;
          autoHoldRef.current = 0;
          ausloesenRef.current(true);
        }
      } catch { /* Frame überspringen */ }
    }, 140);
    return () => clearInterval(iv);
  }, [bleibtOffen, ready, demo, autoFoto, hudAn]);

  const hudFarbe = hud.status === 'bereit' ? '#00e676' : hud.status === 'nah' ? '#ffd740' : hud.status === 'fokus' ? '#4dd0e1' : 'rgba(255,255,255,0.45)';
  const hudText = hudTexte ? hudTexte(`hud_${hud.status}`) : '';
  const ecke = (top, left) => ({
    position: 'absolute',
    width: 22,
    height: 22,
    ...(top ? { top: -2 } : { bottom: -2 }),
    ...(left ? { left: -2 } : { right: -2 }),
    borderColor: hudFarbe,
    borderStyle: 'solid',
    borderWidth: 0,
    ...(top ? { borderTopWidth: 4 } : { borderBottomWidth: 4 }),
    ...(left ? { borderLeftWidth: 4 } : { borderRightWidth: 4 }),
    borderRadius: 4,
    boxShadow: hud.status === 'bereit' ? `0 0 12px ${hudFarbe}` : 'none',
    transition: 'border-color 0.15s ease',
  });

  const hudOverlay = bleibtOffen && hudAn ? (
    <div
      data-testid="tp-hud-frame"
      data-status={hud.status}
      style={{
        position: 'absolute',
        left: `${((hud.ox + hud.x * hud.rw) * 100).toFixed(1)}%`,
        top: `${((hud.oy + hud.y * hud.rh) * 100).toFixed(1)}%`,
        width: `${(hud.w * hud.rw * 100).toFixed(1)}%`,
        height: `${(hud.h * hud.rh * 100).toFixed(1)}%`,
        border: hud.status === 'suche' ? '1.5px dashed rgba(255,255,255,0.3)' : `1.5px solid ${hudFarbe}66`,
        borderRadius: 10,
        background: hud.status === 'bereit' ? 'rgba(0,230,118,0.08)' : 'transparent',
        pointerEvents: 'none',
        transition: 'left 0.12s ease-out, top 0.12s ease-out, width 0.12s ease-out, height 0.12s ease-out, background 0.15s ease',
        boxSizing: 'border-box',
      }}
    >
      <span style={ecke(true, true)} />
      <span style={ecke(true, false)} />
      <span style={ecke(false, true)} />
      <span style={ecke(false, false)} />
      {hud.status === 'bereit' && (
        <div style={{
          position: 'absolute', left: '6%', right: '6%', top: '50%', height: 2,
          background: 'linear-gradient(90deg, transparent, #00e676, transparent)',
          boxShadow: '0 0 8px #00e676', opacity: 0.85,
        }} />
      )}
      {hudText && (
        <div
          data-testid="tp-hud-badge"
          style={{
            position: 'absolute', left: '50%', bottom: -28, transform: 'translateX(-50%)',
            whiteSpace: 'nowrap', padding: '3px 10px', borderRadius: 999,
            background: 'rgba(0,0,0,0.75)', border: `1px solid ${hudFarbe}`,
            color: hud.status === 'suche' ? '#cfd8dc' : hudFarbe,
            fontSize: 11.5, fontWeight: 700, letterSpacing: '.02em',
          }}
        >
          {hudText}{autoFoto && hud.hold > 0 ? ` (${Math.min(100, Math.round((hud.hold / 6) * 100))}%)` : ''}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 8000, background: '#000',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: bleibtOffen ? 'flex-start' : 'center',
      ...(bleibtOffen ? { overflowY: 'auto' } : {}),
    }}>
      {hint && (
        <div data-testid="inapp-hint" style={{ color: '#fff', fontSize: 15, fontWeight: 600, padding: '10px 16px', textAlign: 'center' }}>{hint}</div>
      )}
      {children}
      <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', background: '#000' }}>
        {demo ? (
          <div data-testid="inapp-demo" style={{ width: '100%', height: videoMaxHeight === '60vh' ? '52vh' : '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(0,229,255,.55)',
            fontSize: 14, letterSpacing: '.3em', background: 'repeating-linear-gradient(0deg, rgba(0,229,255,.05) 0 2px, transparent 2px 6px), radial-gradient(circle, #10303a, #000 70%)' }}>DEMO</div>
        ) : (
          <video ref={videoRef} muted playsInline
            style={{ width: '100%', maxHeight: videoMaxHeight || (bleibtOffen ? '46vh' : '68vh'), objectFit: 'contain', background: '#000', display: 'block' }} />
        )}
        {hudOverlay}
      </div>
      {blitz && <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.55)', pointerEvents: 'none' }} />}
      {error && (
        <div style={{ color: '#ffd8a8', padding: '12px 20px', fontSize: 14, textAlign: 'center', lineHeight: 1.45 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 14, paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
        <button type="button" onClick={onClose} style={{
          background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 24,
          padding: '12px 20px', fontSize: 15, cursor: 'pointer', touchAction: 'manipulation',
        }} data-testid="inapp-cancel">{cancelLabel || t('common.cancel')}</button>
        <button
          type="button"
          data-testid="inapp-shutter"
          onPointerDown={!bleibtOffen ? () => ausloesen(false) : undefined}
          onClick={() => ausloesen(false)}
          disabled={!ready || loest}
          aria-label={t('camera.takePhoto')}
          style={{
            width: 74, height: 74, borderRadius: '50%', border: `5px solid ${loest ? '#ffd740' : (bleibtOffen && hudAn && hud.status === 'bereit' ? '#00e676' : '#fff')}`,
            background: ready && !loest ? '#fff' : '#666', cursor: ready ? 'pointer' : 'default',
            colorScheme: 'only light', touchAction: 'manipulation',
            boxShadow: bleibtOffen && hudAn && hud.status === 'bereit' ? '0 0 16px rgba(0,230,118,0.7), 0 0 0 3px rgba(0,0,0,0.4) inset' : '0 0 0 3px rgba(0,0,0,0.4) inset',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}
        />
        {bleibtOffen && hudTexte && (
          <button
            type="button"
            data-testid="tp-autoshutter"
            aria-pressed={autoFoto}
            onClick={() => {
              const next = !autoFoto;
              autoHoldRef.current = 0;
              autoGesperrtRef.current = false;
              if (onToggleAutoFoto) onToggleAutoFoto(next);
              else { setAutoFotoIntern(next); setTurboAutoFotoOn(next); }
            }}
            style={{
              background: autoFoto ? 'rgba(0,230,118,0.22)' : 'rgba(255,255,255,0.12)',
              color: autoFoto ? '#00e676' : '#cfd8dc',
              border: `1.5px solid ${autoFoto ? '#00e676' : 'rgba(255,255,255,0.25)'}`,
              borderRadius: 24,
              padding: '10px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {hudTexte('autoFoto')}
          </button>
        )}
        {torchMoeglich && (
          <button type="button" data-testid="inapp-torch" aria-pressed={torchAn} aria-label="🔦" onClick={() => schalteTorch(!torchAn)} style={{
            width: 54, height: 54, borderRadius: '50%', border: 'none', fontSize: 24, cursor: 'pointer',
            background: torchAn ? '#ffd43b' : 'rgba(255,255,255,0.15)', color: torchAn ? '#000' : '#fff',
          }}>🔦</button>
        )}
      </div>
      {unten}
    </div>
  );
}
